/**
 * pages/accounting/CashBankReport.jsx
 * Cash Report + Bank-wise Reports with running balance
 * ADDED: "Print All" button — fetches Summary + R&P then opens combined print window
 */
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { RefreshCw, Wallet, Building2, FileText, FileSpreadsheet, Printer } from 'lucide-react';
import { downloadCashBankPDF, downloadCashBankExcel, downloadPrintAllPDF } from '../../utils/exportUtils';
import { getCashReport, getBankReport, getSummaryReport, getReceiptPaymentReport } from '../../api/yea.api';
import Button from '../../components/common/Button';
import '../accounting/yea.css';

const fmt = n => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

const RunningTable = ({ rows, title }) => (
  <div className="card" style={{ padding:0, overflow:'hidden', marginBottom:'1.5rem' }}>
    <div style={{ padding:'0.85rem 1.25rem', background:'var(--bg-raised)', borderBottom:'1px solid var(--border-subtle)', fontWeight:600, fontSize:'0.875rem' }}>{title}</div>
    <table className="ledger-table">
      <thead>
        <tr>
          <th>Date</th>
          <th>Description</th>
          <th>Ref</th>
          <th className="amount-cell">Debit (₹)</th>
          <th className="amount-cell">Credit (₹)</th>
          <th className="amount-cell">Balance (₹)</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} style={r.type === 'opening' ? { background:'var(--bg-overlay)' } : {}}>
            <td style={{ whiteSpace:'nowrap', fontSize:'0.82rem' }}>
              {r.date ? new Date(r.date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric',timeZone:'Asia/Kolkata'}) : '—'}
            </td>
            <td style={{ fontSize:'0.875rem', color: r.type === 'opening' ? 'var(--gold)' : 'var(--text-secondary)' }}>{r.description}</td>
            <td style={{ fontFamily:'monospace', fontSize:'0.78rem', color:'var(--text-muted)' }}>{r.receiptNo || r.voucherNo || ''}</td>
            <td className="amount-cell negative-amount">{r.debit > 0 ? fmt(r.debit) : '—'}</td>
            <td className="amount-cell positive-amount">{r.credit > 0 ? fmt(r.credit) : '—'}</td>
            <td className="amount-cell" style={{ fontWeight:600, color: r.balance < 0 ? 'var(--danger)' : 'var(--text)' }}>{fmt(r.balance)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CashBankReport = ({ societyId, fyId, banks, fyName, societyName }) => {
  const [cashReport,   setCashReport]   = useState(null);
  const [bankReports,  setBankReports]  = useState({});
  const [loading,      setLoading]      = useState(true);
  const [printLoading, setPrintLoading] = useState(false);
  const [activeView,   setActiveView]   = useState('cash');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const cashR = await getCashReport(societyId, fyId);
      setCashReport(cashR.data.cashReport);

      const bankResults = {};
      for (const bank of (banks || [])) {
        try {
          const r = await getBankReport(societyId, fyId, bank._id);
          bankResults[bank._id] = r.data.bankReport;
        } catch { /* skip if bank has no transactions */ }
      }
      setBankReports(bankResults);
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  }, [societyId, fyId, banks]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handlePrintAll = async () => {
    setPrintLoading(true);
    try {
      // Fetch Summary and R&P — Cash/Bank already loaded
      const [summaryRes, rpRes] = await Promise.all([
        getSummaryReport(societyId, fyId),
        getReceiptPaymentReport(societyId, fyId),
      ]);
      downloadPrintAllPDF({
        cashReport,
        bankReports,
        banks,
        summaryReport: summaryRes.data.summary,
        rpReport:      rpRes.data.report,
        fyName,
        societyName,
      });
    } catch {
      toast.error('Failed to generate combined report');
    } finally {
      setPrintLoading(false);
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div className="animate-in">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' }}>
        <h2>Cash &amp; Bank Reports</h2>
        <div style={{display:'flex',gap:'0.5rem',flexWrap:'wrap'}}>
          <Button variant="ghost" size="sm" icon={FileText} onClick={() => {
            if (activeView === 'cash' && cashReport) downloadCashBankPDF(cashReport, 'cash', fyName||'FY', societyName||'Society');
            else if (activeView !== 'cash' && bankReports[activeView]) downloadCashBankPDF(bankReports[activeView], 'bank', fyName||'FY', societyName||'Society');
            else toast.error('No data to export');
          }}>PDF</Button>
          <Button variant="ghost" size="sm" icon={FileSpreadsheet} onClick={() => {
            if (activeView === 'cash' && cashReport) downloadCashBankExcel(cashReport, 'cash', fyName||'FY', societyName||'Society');
            else if (activeView !== 'cash' && bankReports[activeView]) downloadCashBankExcel(bankReports[activeView], 'bank', fyName||'FY', societyName||'Society');
            else toast.error('No data to export');
          }}>Excel</Button>
          <Button variant="ghost" size="sm" icon={RefreshCw} onClick={fetchAll}>Refresh</Button>
          {/* Print All — fetches Summary + R&P then opens combined print window */}
          <Button
            size="sm"
            icon={Printer}
            loading={printLoading}
            onClick={handlePrintAll}
            style={{ background:'#C75B39', color:'#fff', border:'none' }}
          >
            Print All
          </Button>
        </div>
      </div>

      {/* View selector */}
      <div style={{ display:'flex', gap:'0.5rem', marginBottom:'1.5rem', flexWrap:'wrap' }}>
        <button
          onClick={() => setActiveView('cash')}
          style={{ display:'flex', alignItems:'center', gap:'0.4rem', padding:'0.4rem 0.9rem', borderRadius:'var(--radius-sm)', border:`1px solid ${activeView==='cash'?'var(--gold)':'var(--border-subtle)'}`, background: activeView==='cash'?'var(--gold-glow)':'var(--bg-raised)', color: activeView==='cash'?'var(--gold)':'var(--text-muted)', cursor:'pointer', fontSize:'0.82rem', fontWeight:500 }}
        >
          <Wallet size={13} /> Cash
        </button>
        {(banks || []).map(b => (
          <button
            key={b._id}
            onClick={() => setActiveView(b._id)}
            style={{ display:'flex', alignItems:'center', gap:'0.4rem', padding:'0.4rem 0.9rem', borderRadius:'var(--radius-sm)', border:`1px solid ${activeView===b._id?'var(--gold)':'var(--border-subtle)'}`, background: activeView===b._id?'var(--gold-glow)':'var(--bg-raised)', color: activeView===b._id?'var(--gold)':'var(--text-muted)', cursor:'pointer', fontSize:'0.82rem', fontWeight:500 }}
          >
            <Building2 size={13} /> {b.bankName}
          </button>
        ))}
      </div>

      {/* Cash */}
      {activeView === 'cash' && cashReport && (
        <div>
          <div style={{ display:'flex', gap:'1rem', marginBottom:'1.25rem' }}>
            <div className="fy-dash-card" style={{ flex:1 }}>
              <div className="fy-dash-label">Opening Balance</div>
              <div className="fy-dash-value">₹{fmt(cashReport.openingBalance)}</div>
            </div>
            <div className="fy-dash-card" style={{ flex:1 }}>
              <div className="fy-dash-label">Closing Balance</div>
              <div className={`fy-dash-value ${cashReport.closingBalance < 0 ? 'negative' : ''}`}>₹{fmt(Math.abs(cashReport.closingBalance))}</div>
            </div>
          </div>
          <RunningTable rows={cashReport.rows} title="Cash Ledger" />
        </div>
      )}

      {/* Bank */}
      {activeView !== 'cash' && bankReports[activeView] && (
        <div>
          <div style={{ display:'flex', gap:'1rem', marginBottom:'1.25rem' }}>
            <div className="fy-dash-card" style={{ flex:1 }}>
              <div className="fy-dash-label">Bank</div>
              <div style={{ fontWeight:600, fontSize:'1rem', color:'var(--text)' }}>{bankReports[activeView].bankName}</div>
            </div>
            <div className="fy-dash-card" style={{ flex:1 }}>
              <div className="fy-dash-label">Opening Balance</div>
              <div className="fy-dash-value">₹{fmt(bankReports[activeView].openingBalance)}</div>
            </div>
            <div className="fy-dash-card" style={{ flex:1 }}>
              <div className="fy-dash-label">Closing Balance</div>
              <div className={`fy-dash-value ${bankReports[activeView].closingBalance < 0 ? 'negative' : ''}`}>₹{fmt(Math.abs(bankReports[activeView].closingBalance))}</div>
            </div>
          </div>
          <RunningTable rows={bankReports[activeView].rows} title={`${bankReports[activeView].bankName} Ledger`} />
        </div>
      )}
    </div>
  );
};

export default CashBankReport;
